import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useCart } from '../context/CartContext';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const TYPE_COLORS = { physical:'#e8a43a', digital:'#4e9cf5', bundle:'#3ecf8e' };

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

export default function ProductDetailPage() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { addItem } = useCart();

  const [product,  setProduct]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selImg,   setSelImg]   = useState(0);
  const [selSize,  setSelSize]  = useState('');
  const [selColor, setSelColor] = useState('');
  const [added,    setAdded]    = useState(false);

  useEffect(() => {
    setLoading(true);
    publicAPI.productBySlug(slug)
      .then(r => { setProduct(r.data?.data); setLoading(false); })
      .catch(err => { if (err.response?.status===404) setNotFound(true); setLoading(false); });
  }, [slug]);

  if (loading) return <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--t-muted)' }}>Loading…</div>;
  if (notFound||!product) return (
    <div style={{ minHeight:'80vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
      <div style={{ fontSize:'3rem', opacity:0.2 }}>◈</div>
      <div style={{ color:'var(--t-muted)', fontSize:'1.1rem' }}>Product not found</div>
      <Link to="/marketplace?tab=products" className="btn btn-outline">← Back to Shop</Link>
    </div>
  );

  const color   = TYPE_COLORS[product.product_type] || 'var(--gold)';
  const icon    = { physical:'◉', digital:'◈', bundle:'◐' }[product.product_type] || '◈';
  const images  = [product.media_url, ...(product.media_urls||[])].filter(Boolean).map(resolveMedia);
  const mainImg = images[selImg] || null;
  const inStock = product.stock_qty === null || product.stock_qty > 0;

  const variant = [selSize, selColor].filter(Boolean).join(' / ') || null;

  const handleAddToCart = () => {
    addItem({
      id:       product.id,
      name:     product.name,
      type:     product.product_type,
      price:    product.is_free ? 0 : Number(product.price),
      currency: product.currency || 'RWF',
      image:    product.media_url,
      slug:     product.slug,
      is_free:  product.is_free,
      variant,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ paddingTop:'calc(var(--nav-h) + 24px)' }}>
        <div className="container">
          <div style={{ display:'flex', gap:8, alignItems:'center', fontSize:'0.8rem', color:'var(--t-muted)', marginBottom:32 }}>
            <Link to="/" style={{ color:'var(--t-muted)', textDecoration:'none' }}>Home</Link>
            <span>›</span>
            <Link to="/marketplace?tab=products" style={{ color:'var(--t-muted)', textDecoration:'none' }}>Shop</Link>
            <span>›</span>
            <span style={{ color:'var(--t-primary)' }}>{product.name}</span>
          </div>
        </div>
      </div>

      <section style={{ paddingBottom:'clamp(60px,8vw,100px)' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1.2fr) minmax(0,1fr)', gap:56, alignItems:'start' }}>

            {/* Images */}
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ aspectRatio:'4/3', borderRadius:20, overflow:'hidden', background:'var(--bg-2)', border:'1px solid var(--border)', position:'relative' }}>
                {mainImg
                  ? <img src={mainImg} alt={product.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'6rem', opacity:0.1 }}>{icon}</div>
                }
                <div style={{ position:'absolute', top:16, left:16, display:'flex', gap:8 }}>
                  <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'5px 12px', borderRadius:20, background:`${color}dd`, color:'#080a0d', textTransform:'capitalize' }}>{icon} {product.product_type}</span>
                  {product.is_featured && <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'5px 12px', borderRadius:20, background:'rgba(232,164,58,0.9)', color:'#080a0d' }}>⭐ Featured</span>}
                  {product.on_sale && product.discount_pct>0 && <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'5px 12px', borderRadius:20, background:'rgba(240,96,96,0.9)', color:'#fff' }}>-{product.discount_pct}%</span>}
                </div>
                {!inStock && (
                  <div style={{ position:'absolute', inset:0, background:'rgba(8,10,13,0.72)', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:20 }}>
                    <span style={{ fontSize:'1rem', fontWeight:600, color:'var(--t-muted)', padding:'10px 24px', background:'var(--bg-2)', borderRadius:10 }}>Out of Stock</span>
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {images.map((img,i)=>(
                    <button key={i} onClick={()=>setSelImg(i)}
                      style={{ width:72, height:72, borderRadius:10, overflow:'hidden', border:`2px solid ${selImg===i?color:'var(--border)'}`, padding:0, cursor:'pointer', flexShrink:0, background:'var(--bg-2)', transition:'all 0.15s' }}>
                      <img src={img} alt={`${product.name} ${i+1}`} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info + CTA */}
            <div style={{ display:'flex', flexDirection:'column', gap:24, position:'sticky', top:'calc(var(--nav-h) + 24px)' }}>

              {product.category && <div style={{ fontSize:'0.65rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.1em' }}>{product.category}</div>}
              <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.6rem,3vw,2.2rem)', fontWeight:700, lineHeight:1.2 }}>{product.name}</h1>

              {/* Price */}
              <div style={{ display:'flex', alignItems:'baseline', gap:12 }}>
                {product.is_free
                  ? <span style={{ fontSize:'1.8rem', fontWeight:700, color:'#3ecf8e' }}>Free</span>
                  : <>
                      <span style={{ fontSize:'1.8rem', fontWeight:700, color }}>{product.currency} {Number(product.price).toLocaleString()}</span>
                      {product.on_sale && product.compare_price && <span style={{ fontSize:'1rem', color:'var(--t-muted)', textDecoration:'line-through' }}>{Number(product.compare_price).toLocaleString()}</span>}
                    </>
                }
              </div>

              {product.short_desc && <p style={{ fontSize:'1rem', color:'var(--t-secondary)', lineHeight:1.75 }}>{product.short_desc}</p>}

              {/* Size selector */}
              {product.sizes?.length > 0 && (
                <div>
                  <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>
                    Size {selSize && <span style={{ color }}> — {selSize}</span>}
                  </div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {product.sizes.map(s=>(
                      <button key={s} onClick={()=>setSelSize(s===selSize?'':s)}
                        style={{ padding:'8px 16px', borderRadius:8, border:`2px solid ${selSize===s?color:'var(--border)'}`, background:selSize===s?`${color}18`:'var(--bg-2)', color:selSize===s?color:'var(--t-secondary)', fontSize:'0.85rem', fontWeight:selSize===s?700:400, cursor:'pointer', transition:'all 0.15s', fontFamily:'inherit' }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Color selector */}
              {product.colors?.length > 0 && (
                <div>
                  <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>
                    Color {selColor && <span style={{ color }}> — {selColor}</span>}
                  </div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {product.colors.map(c=>(
                      <button key={c} onClick={()=>setSelColor(c===selColor?'':c)}
                        style={{ padding:'8px 16px', borderRadius:8, border:`2px solid ${selColor===c?color:'var(--border)'}`, background:selColor===c?`${color}18`:'var(--bg-2)', color:selColor===c?color:'var(--t-secondary)', fontSize:'0.85rem', fontWeight:selColor===c?700:400, cursor:'pointer', transition:'all 0.15s', fontFamily:'inherit' }}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Tech stack */}
              {product.tech_stack?.length > 0 && (
                <div>
                  <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Built With</div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {product.tech_stack.map(t=>(
                      <span key={t} style={{ padding:'4px 10px', borderRadius:20, fontSize:'0.75rem', background:`${color}14`, color, border:`1px solid ${color}33`, fontWeight:500 }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock */}
              {product.product_type==='physical' && product.stock_qty !== null && (
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.82rem' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:inStock?'#3ecf8e':'#ef4444', flexShrink:0 }}/>
                  <span style={{ color:inStock?'#3ecf8e':'#ef4444', fontWeight:500 }}>
                    {inStock ? `${product.stock_qty} in stock` : 'Out of stock'}
                  </span>
                </div>
              )}

              {/* CTA buttons */}
              <div style={{ display:'flex', flexDirection:'column', gap:10, paddingTop:8 }}>
                <button onClick={handleAddToCart} disabled={!inStock}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'15px', fontSize:'1rem', fontWeight:700, borderRadius:12, border:`2px solid ${color}`, background:added?color:`${color}14`, color:added?'#080a0d':color, cursor:!inStock?'not-allowed':'pointer', opacity:!inStock?0.5:1, transition:'all 0.2s', fontFamily:'inherit' }}>
                  {added ? '✓ Added to Cart!' : `🛒 Add to Cart`}
                </button>

                <button onClick={handleBuyNow} disabled={!inStock}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'15px', fontSize:'1rem', fontWeight:700, borderRadius:12, border:'none', background:color, color:'#080a0d', cursor:!inStock?'not-allowed':'pointer', opacity:!inStock?0.5:1, transition:'all 0.15s', fontFamily:'inherit' }}
                  onMouseEnter={e=>{ if(inStock) e.currentTarget.style.filter='brightness(1.1)'; }}
                  onMouseLeave={e=>e.currentTarget.style.filter='none'}>
                  {product.is_free ? 'Get for Free' : 'Buy Now →'}
                </button>

                {product.preview_url && (
                  <a href={product.preview_url} target="_blank" rel="noopener noreferrer"
                    className="btn btn-outline" style={{ justifyContent:'center', padding:'14px' }}>
                    Preview / Demo
                  </a>
                )}
              </div>

              {/* Trust note */}
              <div style={{ padding:'14px 16px', background:'var(--bg-2)', borderRadius:10, border:'1px solid var(--border)', fontSize:'0.78rem', color:'var(--t-muted)', lineHeight:1.7 }}>
                {product.product_type==='digital'
                  ? '🔒 Secure checkout · Digital delivery via email · MilleHills support included'
                  : '📦 Orders confirmed within 24hrs · Delivery across Rwanda · WhatsApp support'}
              </div>
            </div>
          </div>

          {/* Description & details */}
          {(product.description || product.tags?.length > 0) && (
            <div style={{ marginTop:64, display:'grid', gridTemplateColumns:'2fr 1fr', gap:40, alignItems:'start' }}>
              {product.description && (
                <div>
                  <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, marginBottom:20, color }}>About This Product</h2>
                  <div style={{ fontSize:'0.95rem', color:'var(--t-secondary)', lineHeight:1.9, whiteSpace:'pre-wrap' }}>{product.description}</div>
                </div>
              )}
              <div style={{ background:'var(--bg-2)', borderRadius:16, border:'1px solid var(--border)', padding:'20px 22px', display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.9rem', marginBottom:4 }}>Product Details</div>
                {[
                  ['Type',     product.product_type],
                  ['Category', product.category],
                  ['SKU',      product.sku],
                  ['Currency', product.currency],
                  ['Stock',    product.stock_qty===null?'Unlimited':product.stock_qty===0?'Out of stock':`${product.stock_qty} units`],
                ].filter(([,v])=>v).map(([k,v])=>(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', paddingBottom:8, borderBottom:'1px solid var(--border)' }}>
                    <span style={{ color:'var(--t-muted)' }}>{k}</span>
                    <span style={{ fontWeight:500, textTransform:'capitalize' }}>{v}</span>
                  </div>
                ))}
                {product.tags?.length > 0 && (
                  <div>
                    <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Tags</div>
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      {product.tags.map(t=>(
                        <span key={t} style={{ fontSize:'0.68rem', padding:'3px 8px', borderRadius:20, background:'var(--bg-3)', color:'var(--t-muted)', border:'1px solid var(--border)' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ marginTop:48 }}>
            <Link to="/marketplace?tab=products" style={{ fontSize:'0.85rem', color:'var(--t-muted)', textDecoration:'none' }}>← Back to Shop</Link>
          </div>
        </div>
      </section>
    </div>
  );
}